import { useAlerts } from "components/Alerts/AlertProvider";
import { UserOption } from "components/MultipleUserPicker";
import pluralize from "pluralize";
import { useCallback } from "react";

/** First selected user id from a single-select picker, or null when cleared. */
export function firstUserId(users: UserOption[] | null): string | null {
  return users && users.length > 0 ? (users[0]?.id ?? null) : null;
}

/**
 * The warning for co-located copies an assignment left on their old assignee, or
 * null when it reached every site. Only setting an assignee can be refused for
 * the assignee's access, so clearing doesn't blame them.
 */
export function skippedCopiesWarning(
  skippedCopyCount: number,
  settingAssignee: boolean
): string | null {
  if (skippedCopyCount === 0) {
    return null;
  }
  const who = settingAssignee ? "you or the assignee" : "you";
  return `Not changed at ${pluralize("other site", skippedCopyCount, true)}: the site is archived, or ${who} can't access it.`;
}

/**
 * Announces a saved assignment in ONE alert, since the alert bar shows only the
 * latest: the success message, or a warning carrying it when copies were skipped.
 */
export function useAnnounceAssignment(): (
  successMessage: string,
  result: { skippedCopyCount: number; settingAssignee: boolean }
) => void {
  const alerts = useAlerts();
  return useCallback(
    (successMessage, { skippedCopyCount, settingAssignee }) => {
      const warning = skippedCopiesWarning(skippedCopyCount, settingAssignee);
      if (warning) {
        alerts.warning(`${successMessage}. ${warning}`);
      } else {
        alerts.success(successMessage);
      }
    },
    [alerts]
  );
}
